/**
 * First-touch signup attribution (playbook WS3.4).
 *
 * Captures where a visitor came from on their FIRST view of a public marketing
 * or signup route, keeps it in localStorage, and hands it to the registration
 * request whenever that eventually happens — which is often days and several
 * sessions later, after the visitor has read a comparison page, left, and come
 * back through a bookmark.
 *
 * First touch, not last: the whole point is to learn which channel introduced a
 * studio to StudioFlow. Overwriting on a later visit would credit the bookmark
 * or a direct type-in for a signup the fair card actually produced. So a stored
 * record is never replaced — see `captureAttribution`.
 *
 * Nothing here is rendered anywhere in the UI. It is written once, read once at
 * registration, and then it is the organisation's row in a weekly SQL query.
 *
 * Privacy: no cookies, no third-party call, no identifier. The values stored are
 * the campaign tags the visitor's own URL carried plus the referring page —
 * which is exactly what the server would have seen in the Referer header anyway.
 */

import { useEffect } from 'react'

export const ATTRIBUTION_STORAGE_KEY = 'sf_attribution'

/** Longest value we will store or send. Keeps a hostile URL out of the DB column. */
const MAX_FIELD_LENGTH = 255

export interface Attribution {
  utmSource?: string
  utmMedium?: string
  utmCampaign?: string
  /** `?ref=` — referral codes shared by founding members (WS11). */
  ref?: string
  /** document.referrer at first touch. */
  referrer?: string
  /** ISO timestamp of the first touch. */
  capturedAt?: string
  /** Path the visitor first landed on — tells a bare referrer apart from a campaign. */
  landingPath?: string
}

function clean(value: string | null | undefined): string | undefined {
  if (!value) return undefined
  const trimmed = value.trim().slice(0, MAX_FIELD_LENGTH)
  return trimmed.length > 0 ? trimmed : undefined
}

/** localStorage throws in Safari private mode and when storage is full. */
function safeRead(): string | null {
  try {
    return localStorage.getItem(ATTRIBUTION_STORAGE_KEY)
  } catch {
    return null
  }
}

function safeWrite(value: string): void {
  try {
    localStorage.setItem(ATTRIBUTION_STORAGE_KEY, value)
  } catch {
    /* private mode / quota — attribution is nice to have, never load-bearing */
  }
}

/** The stored first-touch record, or null if there is none (or it is corrupt). */
export function readAttribution(): Attribution | null {
  const raw = safeRead()
  if (!raw) return null
  try {
    const parsed = JSON.parse(raw)
    return parsed && typeof parsed === 'object' ? (parsed as Attribution) : null
  } catch {
    // Someone hand-edited it, or an older format. Drop it rather than throw
    // during registration, which is the one place this data is read.
    return null
  }
}

/**
 * Record first touch, if this is one.
 *
 * No-ops when a record already exists (first touch wins) and when the current
 * URL carries no campaign tags AND there is no referrer — a direct type-in with
 * nothing to learn is not worth a storage entry that would then block a real
 * campaign visit from being recorded later.
 *
 * @returns the stored record after the call, for tests and callers that care.
 */
export function captureAttribution(
  search: string = window.location.search,
  referrer: string = document.referrer,
  pathname: string = window.location.pathname,
  now: Date = new Date(),
): Attribution | null {
  const existing = readAttribution()
  if (existing) return existing

  const params = new URLSearchParams(search)
  const attribution: Attribution = {
    utmSource: clean(params.get('utm_source')),
    utmMedium: clean(params.get('utm_medium')),
    utmCampaign: clean(params.get('utm_campaign')),
    ref: clean(params.get('ref')),
    referrer: clean(sameOriginStripped(referrer)),
  }

  const hasSignal = Object.values(attribution).some((v) => v !== undefined)
  if (!hasSignal) return null

  attribution.capturedAt = now.toISOString()
  attribution.landingPath = clean(pathname)

  safeWrite(JSON.stringify(attribution))
  return attribution
}

/**
 * Drops a referrer that is just StudioFlow linking to itself. An internal
 * navigation is not a first touch, and storing one would permanently block the
 * real external referrer from ever being recorded for this browser.
 */
function sameOriginStripped(referrer: string): string | undefined {
  if (!referrer) return undefined
  try {
    return new URL(referrer).origin === window.location.origin ? undefined : referrer
  } catch {
    return undefined
  }
}

/**
 * Mount on every public marketing and signup route. Runs once per mount and is
 * a no-op after the first touch, so mounting it in several places is safe and
 * is in fact the point — a visitor's first StudioFlow page might be the landing
 * page, a comparison page, or /register straight from a short link.
 */
export function useAttributionCapture(): void {
  useEffect(() => {
    captureAttribution()
  }, [])
}

/** Forget the stored record. Called after a successful registration. */
export function clearAttribution(): void {
  try {
    localStorage.removeItem(ATTRIBUTION_STORAGE_KEY)
  } catch {
    /* see safeWrite */
  }
}

/**
 * The attribution fields as they go into the `/auth/register-org` body.
 * Returns an empty object when there is nothing stored, so the caller can
 * spread it unconditionally and omitted fields simply stay null on the org.
 */
export function attributionPayload(): Partial<Attribution> {
  const stored = readAttribution()
  if (!stored) return {}
  return {
    utmSource: stored.utmSource,
    utmMedium: stored.utmMedium,
    utmCampaign: stored.utmCampaign,
    ref: stored.ref,
    referrer: stored.referrer,
    landingPath: stored.landingPath,
  }
}
